import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Download, FileSpreadsheet, FileText, Printer, ChevronDown, Mail } from "lucide-react";
import { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";

const REPORT_KEY_FROM_ENDPOINT = {
  "/tenants/export.csv": "tenants",
  "/rents/export.csv": "rents",
  "/expenses/export.csv": "expenses",
  "/maintenance/export.csv": "maintenance",
  "/financials/pnl/export": "pnl",
};

export default function ExportMenu({
  endpoint, filenameBase, extraParams = {}, onPrint, disabled, testidPrefix = "export", label = "Export",
}) {
  const today = new Date().toISOString().slice(0, 10);
  const reportKey = REPORT_KEY_FROM_ENDPOINT[endpoint];
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailForm, setEmailForm] = useState({ to_email: "", format: "pdf", subject: "", message: "" });
  const [sending, setSending] = useState(false);

  const download = async (format) => {
    try {
      const params = { format, ...extraParams };
      const res = await api.get(endpoint, { params, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const ext = format === "xlsx" ? "xlsx" : format === "pdf" ? "pdf" : "csv";
      const a = document.createElement("a");
      a.href = url; a.download = `${filenameBase}-${today}.${ext}`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(`${format.toUpperCase()} downloaded`);
    } catch { toast.error("Download failed"); }
  };

  const handlePrint = () => { if (onPrint) onPrint(); else window.print(); };

  const sendEmail = async () => {
    if (!emailForm.to_email) { toast.error("Recipient email is required"); return; }
    setSending(true);
    try {
      const payload = { report: reportKey, format: emailForm.format, to_email: emailForm.to_email,
                        subject: emailForm.subject, message: emailForm.message, ...extraParams };
      const { data } = await api.post("/reports/email", payload);
      toast.success(`Sent to ${data.to}`);
      setEmailOpen(false);
      setEmailForm({ to_email: "", format: "pdf", subject: "", message: "" });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Email failed");
    }
    setSending(false);
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button data-testid={`${testidPrefix}-menu`} variant="outline" disabled={disabled} className="gap-2">
            <Download className="h-4 w-4" /> {label} <ChevronDown className="h-3.5 w-3.5 opacity-60" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-52">
          <DropdownMenuItem data-testid={`${testidPrefix}-xlsx`} onClick={() => download("xlsx")} className="gap-2 cursor-pointer">
            <FileSpreadsheet className="h-4 w-4 text-emerald-600" /> Excel (.xlsx)
          </DropdownMenuItem>
          <DropdownMenuItem data-testid={`${testidPrefix}-pdf`} onClick={() => download("pdf")} className="gap-2 cursor-pointer">
            <FileText className="h-4 w-4 text-rose-600" /> PDF
          </DropdownMenuItem>
          <DropdownMenuItem data-testid={`${testidPrefix}-csv`} onClick={() => download("csv")} className="gap-2 cursor-pointer">
            <Download className="h-4 w-4 text-slate-600" /> CSV
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem data-testid={`${testidPrefix}-print`} onClick={handlePrint} className="gap-2 cursor-pointer">
            <Printer className="h-4 w-4 text-slate-700" /> Print
          </DropdownMenuItem>
          {reportKey && (
            <DropdownMenuItem data-testid={`${testidPrefix}-email`} onClick={() => setEmailOpen(true)} className="gap-2 cursor-pointer">
              <Mail className="h-4 w-4 text-blue-600" /> Email…
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={emailOpen} onOpenChange={setEmailOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Email this report</DialogTitle></DialogHeader>
          <div className="space-y-3 pt-2">
            <div className="space-y-1.5">
              <Label className="text-xs">To (email address)</Label>
              <Input data-testid={`${testidPrefix}-email-to`} type="email" required value={emailForm.to_email} onChange={e => setEmailForm({ ...emailForm, to_email: e.target.value })} placeholder="accountant@example.com" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Attachment format</Label>
              <Select value={emailForm.format} onValueChange={(v) => setEmailForm({ ...emailForm, format: v })}>
                <SelectTrigger data-testid={`${testidPrefix}-email-format`}><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="pdf">PDF</SelectItem>
                  <SelectItem value="xlsx">Excel (.xlsx)</SelectItem>
                  <SelectItem value="csv">CSV</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Subject (optional)</Label>
              <Input value={emailForm.subject} onChange={e => setEmailForm({ ...emailForm, subject: e.target.value })} placeholder="Auto-filled if blank" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Message (optional)</Label>
              <Textarea rows={3} value={emailForm.message} onChange={e => setEmailForm({ ...emailForm, message: e.target.value })} placeholder="Add a personal note for the recipient…" />
            </div>
            <p className="text-xs text-slate-500">Resend sandbox: emails only deliver to your own account email until you verify a domain at resend.com/domains.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailOpen(false)}>Cancel</Button>
            <Button data-testid={`${testidPrefix}-email-send`} onClick={sendEmail} disabled={sending} className="bg-slate-900 hover:bg-slate-800 gap-1.5">
              <Mail className="h-3.5 w-3.5" /> {sending ? "Sending…" : "Send"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
